import { Component, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import {
  AbstractComponent,
  ChatCommandTypeEnum,
  ChatDiscoverMeta,
  ChatDiscoveredChannel,
  ChatSocketService,
} from 'lib-trend-core';
import { Observable, Subject, combineLatest } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import * as ChatActions from '../../state/chat.actions';
import {
  discoverLoadingSelector,
  discoverMetaSelector,
  discoveredSelector,
} from '../../state/chat.selectors';

/** Page size requested from `GET /api/chat/channels/discover`. */
const DISCOVER_PAGE_SIZE = 20;

/** Directory row — precomputed so the template does no per-CD work. */
interface ChatBrowseRow {
  id: string;
  name: string;
  description: string;
  memberCount: number;
  isMember: boolean;
  archived: boolean;
}

/**
 * Internal Chat "Explorar canais" dialog (SPEC-2026-07-internal-chat, T-35 ·
 * RF-03/AC-03).
 *
 * The public-channel directory: the read comes from the existing
 * `discoverChannels` store plumbing (`GET /api/chat/channels/discover`, T-14),
 * paginated with the endpoint meta (page/perPage/total/hasMore) and filtered by
 * a debounced search box. Joining sends CHANNEL_JOIN over the websocket command
 * path — on ack the row flips to "joined" and the sidebar picks the channel up
 * through the `joined` fan-out plus the bootstrap refresh effect.
 *
 * PUBLIC channels only: PRIVATE channels and GROUPs are invite-only server-side
 * (`chat.errors.privateChannel`), so they are never listed here.
 */
@Component({
  templateUrl: './chat-channel-browse-dialog.component.html',
  styleUrls: ['./chat-channel-browse-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ChatChannelBrowseDialogComponent extends AbstractComponent implements OnInit {

  rows: ChatBrowseRow[] = [];
  meta: ChatDiscoverMeta | null = null;
  searchTerm = '';

  /** Channel whose CHANNEL_JOIN is in flight — disables just that row. */
  joiningId: string | null = null;

  loading$: Observable<boolean> = this.store.select(discoverLoadingSelector);

  private readonly terms = new Subject<string>();
  private page = 1;

  constructor(
    injector: Injector,
    private readonly dialogRef: MatDialogRef<ChatChannelBrowseDialogComponent>,
    private readonly store: Store,
    private readonly chatSocketService: ChatSocketService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    combineLatest([
      this.store.select(discoveredSelector),
      this.store.select(discoverMetaSelector),
    ])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([channels, meta]) => {
        this.rows = channels.map((channel) => this.buildRow(channel));
        this.meta = meta;
      });

    this.terms
      .pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((term) => this.load(term, 1, false));

    this.load('', 1, false);
  }

  applyFilter(): void {
    this.terms.next(this.searchTerm.trim());
  }

  get hasMore(): boolean {
    return this.meta?.hasMore === true;
  }

  get total(): number {
    return this.meta?.total ?? this.rows.length;
  }

  loadMore(): void {
    this.page += 1;
    this.load(this.searchTerm.trim(), this.page, true);
  }

  /** CHANNEL_JOIN (RF-03/RF-04) — self-service join, PUBLIC channels only. */
  join(row: ChatBrowseRow): void {
    if (row.isMember || this.joiningId) return;

    this.joiningId = row.id;
    this.chatSocketService.sendCommand(ChatCommandTypeEnum.CHANNEL_JOIN, { channelId: row.id })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.joiningId = null;
          // Keep the open directory honest; the conversation itself arrives via
          // the `joined` fan-out and the bootstrap refresh effect.
          this.store.dispatch(ChatActions.applyDiscoveredJoin({ channelId: row.id }));
          this.store.dispatch(ChatActions.loadBootstrap());
        },
        error: (ack) => {
          this.joiningId = null;
          this.alertService.error(this.resolveAckError(ack?.errorKey));
        },
      });
  }

  /**
   * Members jump straight into the conversation; non-members land on the
   * read-only preview with the "Entrar no canal" call-to-action (DEV-1984 —
   * the bootstrap lists every public channel, so the pane can always resolve it).
   */
  open(row: ChatBrowseRow): void {
    this.store.dispatch(ChatActions.openConversation({ channelId: row.id }));
    this.dialogRef.close(true);
  }

  close(): void {
    this.dialogRef.close();
  }

  trackRow(_index: number, row: ChatBrowseRow): string {
    return row.id;
  }

  private load(search: string, page: number, append: boolean): void {
    this.page = page;
    this.store.dispatch(ChatActions.discoverChannels({
      search: search || undefined,
      page,
      perPage: DISCOVER_PAGE_SIZE,
      append,
    }));
  }

  private buildRow(channel: ChatDiscoveredChannel): ChatBrowseRow {
    return {
      id: channel._id,
      name: channel.name ?? this.t('chat.bubble.channelConversation'),
      description: channel.description ?? '',
      memberCount: channel.memberCount ?? 0,
      isMember: channel.isMember === true,
      archived: channel.archived === true,
    };
  }

  /** Translates a backend/synthetic ack errorKey, falling back when unknown. */
  private resolveAckError(errorKey?: string): string {
    if (errorKey) {
      const translated = this.t(errorKey);
      if (translated !== errorKey) return translated;
    }
    return this.t('chat.errors.commandFailed');
  }
}
